import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Form,
  Button,
  InputGroup,
  Spinner,
  Badge,
  Alert,
} from "react-bootstrap";

import JewelleryFormInline from "./JewelleryFormInline";
import TryOnModal from "./TryOnModal";
import { useSearchParams } from "react-router-dom";

export default function ChatWindow({
  apiBase = "/api",
  customerId,
  sessionId,
  setSessionId,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showTryOn, setShowTryOn] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }

    const fetchSession = async () => {
      try {
        const res = await fetch(`${apiBase}/sessions/${sessionId}/`);
        const data = await res.json();

        const formatted = data.messages.map((m) => ({
          role: m.role,
          content: m.content,
          images: (m.images || []).map((img) => img.url),
        }));

        setMessages(formatted);
      } catch (err) {
        console.error(err);
      }
    };

    fetchSession();
  }, [sessionId, apiBase]);

  const handleInitialGenerate = async (data) => {
    if (!customerId) {
      setError("Please select a customer first");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let res;

      // -----------------------------
      // IMAGE UPLOAD FLOW
      // -----------------------------
      if (data.referenceImage) {
        const formData = new FormData();

        formData.append("prompt", data.prompt);
        formData.append("customer_id", customerId);

        if (data.referenceImage) {
          formData.append("reference_image", data.referenceImage);
        }

        res = await fetch(`${apiBase}/images/generate/`, {
          method: "POST",
          body: formData,
        });
      }

      // -----------------------------
      // NORMAL TEXT FLOW
      // -----------------------------
      else {
        res = await fetch(`${apiBase}/images/generate/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: data.prompt,
            customer_id: customerId,
          }),
        });
      }

      const responseData = await res.json();

      if (!res.ok) {
        throw responseData;
      }

      setSessionId(responseData.session_id);

      setMessages([
        {
          role: "user",
          content: data.prompt,
        },
        {
          role: "assistant",
          content: "Generated design",
          images: [responseData.image_url],
        },
      ]);
    } catch (err) {
      console.error(err);

      setError(err.error || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const prompt = input;

    setInput("");
    setLoading(true);

    setMessages((m) => [...m, { role: "user", content: prompt }]);

    try {
      const res = await fetch(`${apiBase}/images/generate/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          customer_id: customerId,
          session_id: sessionId,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw data;

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Updated design",
          images: [data.image_url],
        },
      ]);
    } catch (err) {
      console.error(err);
      setError(err.error || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const openTryOn = (img) => {
    setSelectedImage(img);
    setShowTryOn(true);
  };

  const handleTryOnGenerated = (generatedImage) => {
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: "Jewellery try-on result",
        images: [generatedImage],
      },
    ]);
  };

  return (
    <>
      <TryOnModal
        show={showTryOn}
        onHide={() => setShowTryOn(false)}
        imageUrl={selectedImage}
        apiBase={apiBase}
        customerId={customerId}
        sessionId={sessionId}
        onGenerated={handleTryOnGenerated}
      />

      {!sessionId ? (
        <JewelleryFormInline
          onGenerate={handleInitialGenerate}
          loading={loading}
        />
      ) : (
        <Card className="shadow-sm">
          <Card.Body style={{ height: "60vh", overflowY: "auto" }}>
            {error && <Alert variant="danger">{error}</Alert>}

            {messages.map((m, idx) => (
              <div key={idx} className="mb-4">
                <Badge bg={m.role === "user" ? "primary" : "secondary"}>
                  {m.role}
                </Badge>

                <div className="mt-2">{m.content}</div>

                {m.images && (
                  <div className="d-flex flex-wrap gap-3 mt-3">
                    {m.images.map((img, i) => (
                      <div key={i}>
                        <img
                          src={img}
                          alt=""
                          style={{
                            width: 320,
                            borderRadius: 8,
                            objectFit: "cover",
                          }}
                        />

                        <div className="mt-2">
                          <Button size="sm" onClick={() => openTryOn(img)}>
                            Try On
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="text-center">
                <Spinner />
              </div>
            )}

            <div ref={bottomRef} />
          </Card.Body>

          <Card.Footer>
            <InputGroup>
              <Form.Control
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Refine this design..."
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />

              <Button onClick={handleSend} disabled={loading}>
                Send
              </Button>
            </InputGroup>
          </Card.Footer>
        </Card>
      )}
    </>
  );
}
